import { Button } from '../../../Button';
import type { ColumnDef } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import useI18n from '../../../../hooks/useI18n';
import { useMemo } from 'react';

export const EXPANDABLE_COLUMN_ID = 'expandable';
const CHEVRON_CLASSES =
  'eye:size-s eye:stroke-[1.5] eye:transition-transform eye:duration-200';

export function useExpandableColumn<TData>(): ColumnDef<TData> {
  const { t } = useI18n();

  return useMemo<ColumnDef<TData>>(
    () => ({
      accessorKey: EXPANDABLE_COLUMN_ID,
      enableColumnFilter: false,
      enableSorting: false,
      enableHiding: false,
      size: 65,
      header: ({ table }) => {
        const isExpanded = table.getIsAllRowsExpanded();

        return (
          <Button
            className='eye:size-s! eye:align-middle eye:outline-offset-[-2px]'
            size='icon'
            variant='ghost'
            aria-label={isExpanded ? t('collapseRows') : t('expandRows')}
            onClick={() => table.toggleAllRowsExpanded(!isExpanded)}
          >
            <ChevronDown
              className={cn(
                CHEVRON_CLASSES,
                isExpanded ? 'eye:rotate-180' : '',
              )}
              aria-hidden='true'
            />
          </Button>
        );
      },
      cell: ({ row }) => {
        const isExpanded = row.getIsExpanded();

        return (
          <Button
            className='eye:size-s! eye:align-middle'
            size='icon'
            variant='ghost'
            aria-label={isExpanded ? t('collapseRow') : t('expandRow')}
            onClick={() => row.toggleExpanded(!isExpanded)}
          >
            <ChevronDown
              className={cn(
                CHEVRON_CLASSES,
                isExpanded ? 'eye:rotate-180' : '',
              )}
              aria-hidden='true'
            />
          </Button>
        );
      },
    }),
    [t],
  );
}

/**
 * @deprecated Use `useExpandableColumn` so the hook dependency is explicit at
 * call sites. Kept as an alias to avoid breaking existing library consumers.
 */
export const ExpandableColumn = useExpandableColumn;
